import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CATEGORY_MAP } from "@/data/catalog";
import { useAuth } from "@/context/AuthContext";
import { Star, MapPin, ShieldCheck, MessageCircle, Phone, Clock, Tag, Flag, ExternalLink } from "lucide-react";

function StarRow({ value, size = 16, onSet }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={onSet ? () => onSet(n) : undefined} disabled={!onSet} className={onSet ? "cursor-pointer" : ""} data-testid={onSet ? `rate-star-${n}` : undefined}>
          <Star style={{ width: size, height: size }} className={`${n <= value ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function BusinessDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [deals, setDeals] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [photoIdx, setPhotoIdx] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get(`/businesses/${id}`);
      setB(data);
      const [rev, dl] = await Promise.all([
        api.get(`/businesses/${data.business_id}/reviews`),
        api.get(`/businesses/${data.business_id}/deals`),
      ]);
      setReviews(rev.data);
      setDeals(dl.data);
    } catch { setB(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // SEO: inject LocalBusiness JSON-LD + <title> when business loads
  useEffect(() => {
    if (!b) return;
    document.title = `${b.name} — ${b.sector} | ApnaIslamabad`;
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: b.name,
      description: b.description,
      image: b.photos?.[0],
      telephone: b.phone,
      priceRange: b.price_range,
      address: { "@type": "PostalAddress", streetAddress: b.address, addressLocality: b.sector, addressCountry: "PK" },
      aggregateRating: b.review_count ? { "@type": "AggregateRating", ratingValue: b.avg_rating, reviewCount: b.review_count } : undefined,
    };
    let script = document.getElementById("biz-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "biz-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
    return () => { document.getElementById("biz-jsonld")?.remove(); };
  }, [b]);

  if (b === null) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-slate-500">Loading…</div>;

  const cat = CATEGORY_MAP[b.category];
  const photos = (b.photos?.length ? b.photos : [cat?.image]).filter(Boolean);
  const waNumber = (b.whatsapp || b.phone || "").replace(/\D/g, "");
  const waMsg = encodeURIComponent(`Assalam-o-Alaikum, ApnaIslamabad se aapki listing dekhi (${b.name}). `);
  const waHref = waNumber ? `https://wa.me/${waNumber.startsWith("92") ? waNumber : "92" + waNumber.replace(/^0/, "")}?text=${waMsg}` : "";

  const submitReview = async () => {
    if (!user) return nav("/login");
    try {
      await api.post("/reviews", { business_id: b.business_id, rating, comment });
      toast.success("Review posted");
      setReviewOpen(false); setComment(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post review");
    }
  };

  const submitClaim = async () => {
    if (!user) return nav("/login");
    try {
      await api.post("/claims", { business_id: b.business_id, message: comment });
      toast.success("Claim submitted — admin will call to verify.");
      setClaimOpen(false); setComment("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit claim");
    }
  };

  const trackWA = () => {
    api.post(`/businesses/${b.business_id}/whatsapp-click`).catch(() => {});
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 lg:pb-8">
      <div className="text-xs text-slate-500 mb-3 truncate">
        <Link to="/" className="hover:text-emerald-700">Home</Link>
        <span className="mx-1">/</span>
        <Link to={`/search?category=${b.category}`} className="hover:text-emerald-700">{cat?.name}</Link>
        <span className="mx-1">/</span>
        <span className="text-slate-700">{b.name}</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
        <div className="lg:col-span-2">
          {/* Gallery */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="aspect-[16/10] bg-slate-100">
              <img src={photos[photoIdx]} alt={b.name} className="w-full h-full object-cover" />
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto no-scrollbar">
                {photos.map((p, i) => (
                  <button key={i} onClick={() => setPhotoIdx(i)} className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 ${i === photoIdx ? "border-emerald-700" : "border-transparent"}`}>
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Header */}
          <div className="mt-5 sm:mt-6 flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {b.verified && <Badge className="bg-emerald-800 hover:bg-emerald-900 text-white flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified</Badge>}
                <Badge variant="secondary" className="capitalize">{cat?.name}</Badge>
                {b.subcategory && <Badge variant="outline" className="capitalize">{b.subcategory.replace(/_/g, " ")}</Badge>}
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>{b.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-emerald-700 flex-shrink-0" /> {b.sector} {b.address && `· ${b.address}`}</span>
                <span className="flex items-center gap-1"><StarRow value={Math.round(b.avg_rating || 0)} /> <span className="font-semibold text-slate-900">{b.avg_rating ? b.avg_rating.toFixed(1) : "New"}</span> <span className="text-slate-500">({b.review_count || 0})</span></span>
                {b.price_range && <Badge variant="secondary">{b.price_range}</Badge>}
              </div>
            </div>
          </div>

          {b.description && (
            <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>About</h2>
              <p className="text-slate-600 whitespace-pre-wrap">{b.description}</p>
            </div>
          )}

          {/* Deals */}
          {deals.length > 0 && (
            <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Tag className="w-5 h-5 text-emerald-700" /> Active deals</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {deals.map((d) => (
                  <div key={d.deal_id} className="rounded-xl bg-gradient-to-br from-emerald-50 to-amber-50 border border-emerald-200 p-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-amber-700">{d.discount || "Offer"}</div>
                    <div className="font-bold text-slate-900 mt-1">{d.title}</div>
                    {d.description && <div className="text-sm text-slate-600 mt-1">{d.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Reviews ({reviews.length})</h2>
              <Button size="sm" onClick={() => user ? setReviewOpen(true) : nav("/login")} className="bg-emerald-800 hover:bg-emerald-900" data-testid="write-review-btn">Write a review</Button>
            </div>
            {reviews.length === 0 ? (
              <p className="text-slate-500 text-sm">No reviews yet — be the first!</p>
            ) : (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r.review_id} className="border-b border-slate-100 pb-4 last:border-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-900">{r.user_name}</div>
                        <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</div>
                      </div>
                      <StarRow value={r.rating} />
                    </div>
                    {r.comment && <p className="text-slate-600 mt-2 text-sm">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sticky CTA sidebar */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              {waHref ? (
                <a href={waHref} target="_blank" rel="noreferrer" onClick={trackWA} data-testid="whatsapp-chat-button" className="w-full flex items-center justify-center gap-2 wa-green text-white font-bold h-12 rounded-lg transition-colors">
                  <MessageCircle className="w-5 h-5" /> WhatsApp Chat
                </a>
              ) : (
                <div className="text-xs text-slate-500 text-center py-2">No WhatsApp on file</div>
              )}
              {b.phone && (
                <a href={`tel:${b.phone}`} data-testid="call-button" className="w-full flex items-center justify-center gap-2 border border-emerald-700 text-emerald-800 font-semibold h-11 rounded-lg hover:bg-emerald-50 transition-colors">
                  <Phone className="w-4 h-4" /> Call {b.phone}
                </a>
              )}
              {b.map_link && (
                <a href={b.map_link} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-emerald-700">
                  <MapPin className="w-4 h-4" /> View on map <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5 text-sm">
              {b.hours && (
                <div className="flex items-start gap-2 mb-3">
                  <Clock className="w-4 h-4 text-emerald-700 mt-0.5" />
                  <div><div className="font-semibold text-slate-900">Hours</div><div className="text-slate-600 whitespace-pre-wrap">{b.hours}</div></div>
                </div>
              )}
              {b.website && (
                <div className="text-slate-600">
                  <span className="font-semibold text-slate-900">Website: </span>
                  <a href={b.website} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">{b.website}</a>
                </div>
              )}
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <div className="text-sm font-semibold text-emerald-900">Is this your business?</div>
              <p className="text-xs text-slate-600 mt-1">Claim your listing to reply to leads, post deals & respond to reviews.</p>
              <Button onClick={() => user ? setClaimOpen(true) : nav("/login")} variant="outline" className="mt-3 w-full border-emerald-700 text-emerald-800 hover:bg-emerald-100" data-testid="claim-listing-btn">Claim this listing</Button>
            </div>

            <button className="w-full flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-red-600" data-testid="report-listing-btn"><Flag className="w-3 h-3" /> Report listing</button>
          </div>
        </aside>
      </div>

      {/* Mobile sticky bottom action bar */}
      {(waHref || b.phone) && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-3 py-2 flex gap-2 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
          {waHref && (
            <a href={waHref} target="_blank" rel="noreferrer" onClick={trackWA} data-testid="mobile-whatsapp-btn" className="flex-1 flex items-center justify-center gap-1.5 wa-green text-white font-semibold h-11 rounded-lg text-sm">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          {b.phone && (
            <a href={`tel:${b.phone}`} data-testid="mobile-call-btn" className={`${waHref ? "" : "flex-1"} flex items-center justify-center gap-1.5 border border-emerald-700 text-emerald-800 font-semibold h-11 rounded-lg px-4 text-sm`}>
              <Phone className="w-4 h-4" /> {waHref ? "" : "Call"}
            </a>
          )}
        </div>
      )}

      {/* Review Modal */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Rate {b.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><div className="text-xs uppercase tracking-widest text-emerald-800 font-bold mb-2">Your rating</div><StarRow value={rating} size={32} onSet={setRating} /></div>
            <Textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" data-testid="review-comment-input" />
          </div>
          <DialogFooter>
            <Button onClick={submitReview} className="bg-emerald-800 hover:bg-emerald-900" data-testid="submit-review-btn">Post review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim Modal */}
      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Claim {b.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Our admin will call you to verify ownership within 48 hours.</p>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Anything we should know? (optional)" data-testid="claim-message-input" />
          <DialogFooter>
            <Button onClick={submitClaim} className="bg-emerald-800 hover:bg-emerald-900" data-testid="submit-claim-btn">Submit claim</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
